import { Button } from "@gigpro/ui/Button";
import { Checkbox } from "@gigpro/ui/Checkbox";
import { Textarea } from "@gigpro/ui/Textarea";
import { toast } from "@gigpro/ui/Toast";
import { convertToTimezone, formatDate, formatDateTimeRange } from "@gigpro/utils/datetime";
import { ApiError } from "@sidegigllc/api";
import {
  type AdminHighDemandSurcharge,
  useAdminHighDemandSurchargesRemoveCreate,
  useAdminHighDemandSurchargesRestoreCreate,
} from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { highDemandQuery } from "./queries";

const REASON_MAX_LENGTH = 500;

const showError = (title: string) => (error: unknown) =>
  toast.error(title, { description: ApiError.fromThrown(error).getMainErrorMessage() });

export function HighDemand({ proId }: Readonly<{ proId: number }>) {
  const { data: shifts = [], refetch, isPending, isError } = useQuery(highDemandQuery({ proId }));
  const [selected, setSelected] = React.useState<ReadonlySet<number>>(new Set());
  const [reason, setReason] = React.useState("");

  const remove = useAdminHighDemandSurchargesRemoveCreate({
    mutation: {
      onSuccess: ({ application_ids }) => {
        toast.success(
          `Surcharge removed from ${application_ids.length} shift${application_ids.length === 1 ? "" : "s"}`,
        );
        setSelected(new Set());
        setReason("");
        refetch();
      },
      onError: showError("Couldn't remove the surcharge"),
    },
  });
  const restore = useAdminHighDemandSurchargesRestoreCreate({
    mutation: {
      onSuccess: () => {
        toast.success("Surcharge restored");
        refetch();
      },
      onError: showError("Couldn't restore the surcharge"),
    },
  });

  const toggle = (applicationId: number, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(applicationId);
      else next.delete(applicationId);
      return next;
    });

  if (isPending) return <p className="p-6 text-gray">Loading...</p>;
  if (isError)
    return <p className="p-6 text-gray">Couldn't load this pro's High Demand shifts. Refresh to try again.</p>;
  if (shifts.length === 0) {
    return <p className="p-6 text-gray">No upcoming shifts with a High Demand surcharge.</p>;
  }

  const weeks = groupByWeek(shifts);
  // A refetch can drop a selected shift or show it removed by someone else; only send what is still removable.
  const selectedIds = shifts
    .filter((shift) => shift.multiplier !== null && selected.has(shift.application_id))
    .map((shift) => shift.application_id);
  const canRemove = selectedIds.length > 0 && reason.trim().length > 0 && !remove.isPending;

  return (
    <div className="flex flex-col gap-4 p-6">
      {weeks.map(([weekStart, weekShifts]) => (
        <section key={weekStart} className="flex flex-col gap-2">
          <h3 className="text-normal font-semibold">Week of {formatDate(new Date(weekStart), "EEE, MMM d")}</h3>
          {weekShifts.map((shift) => (
            <ShiftRow
              key={shift.application_id}
              shift={shift}
              selected={selected.has(shift.application_id)}
              onToggle={(on) => toggle(shift.application_id, on)}
              onRestore={() => restore.mutate({ data: { application_ids: [shift.application_id] } })}
              isRestoring={restore.isPending && restore.variables?.data.application_ids[0] === shift.application_id}
            />
          ))}
        </section>
      ))}
      {selectedIds.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-light-gray bg-white p-4">
          <p className="text-small">
            The business is charged the normal rate for the {selectedIds.length} selected shift
            {selectedIds.length === 1 ? "" : "s"}.
          </p>
          <Textarea
            aria-label="Reason for removing the surcharge"
            placeholder="e.g. Business disputed these shifts, hours were booked in error"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX_LENGTH}
          />
          <Button
            className="self-end"
            variant="contained"
            disabled={!canRemove}
            loading={remove.isPending}
            onClick={() => remove.mutate({ data: { application_ids: selectedIds, reason: reason.trim() } })}
          >
            Remove surcharge
          </Button>
        </div>
      )}
    </div>
  );
}

function ShiftRow({
  shift,
  selected,
  onToggle,
  onRestore,
  isRestoring,
}: Readonly<{
  shift: AdminHighDemandSurcharge;
  selected: boolean;
  onToggle: (on: boolean) => void;
  onRestore: () => void;
  isRestoring: boolean;
}>) {
  const start = convertToTimezone(shift.start_time, shift.timezone);
  const end = convertToTimezone(shift.end_time, shift.timezone);
  const when = formatDateTimeRange(start, end, "EEE, MMM d", shift.timezone);
  const isRemoved = shift.multiplier === null;
  return (
    <div className="flex items-center gap-3 rounded-md border border-light-gray bg-white px-3 py-2 text-small">
      {isRemoved ? (
        <span className="w-4" />
      ) : (
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onToggle(checked === true)}
          aria-label={`Select shift ${shift.application_id} for surcharge removal`}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-black">
          {shift.position} at {shift.location_name}
        </div>
        <div className="text-gray">{when}</div>
      </div>
      {isRemoved ? (
        <>
          <div className="max-w-md text-right text-gray">
            Removed by {shift.removed_by}
            {shift.removed_at && ` on ${formatDate(new Date(shift.removed_at), "MMM d")}`}: {shift.removal_reason}
          </div>
          <Button size="small" variant="outlined" onClick={onRestore} loading={isRestoring} disabled={isRestoring}>
            Restore
          </Button>
        </>
      ) : (
        <span className="font-semibold text-black">{shift.multiplier}× High Demand</span>
      )}
    </div>
  );
}

function groupByWeek(shifts: ReadonlyArray<AdminHighDemandSurcharge>) {
  const weeks = new Map<string, Array<AdminHighDemandSurcharge>>();
  for (const shift of shifts) {
    const key = new Date(shift.week_start).toISOString();
    weeks.set(key, [...(weeks.get(key) ?? []), shift]);
  }
  return [...weeks.entries()];
}
